/**
 * Admin -> Edge Fleet -> Install tokens -> Extend (release 2.1 item 21).
 *
 * A rollout to thousands of laptops takes weeks, and a fleet token's enrolment
 * window used to be fixed at mint (24 hours at most). Extending it here keeps
 * the SAME token - and therefore the same Intune package - working for the
 * next wave. The device limit can be raised here too; the service used to tell
 * operators to "raise max_devices" with no way to do it.
 *
 * Both are audited server-side with the before and after values and the
 * reason typed here.
 *
 * Backend: PATCH /v1/edge/install-tokens/:id { ttlSeconds?, maxDevices?, reason }
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { updateEdgeInstallToken, type EdgeInstallTokenRow } from '@/lib/api';

const DAY = 24 * 60 * 60;
const WINDOW_OPTIONS: ReadonlyArray<{ seconds: number | null; label: string }> = [
  { seconds: null, label: 'Leave as it is' },
  { seconds: 7 * DAY, label: '7 days from now' },
  { seconds: 30 * DAY, label: '30 days from now' },
  { seconds: 90 * DAY, label: '90 days from now' },
];

export function InstallTokenExtendForm({
  token,
  onDone,
  onCancel,
}: {
  token: EdgeInstallTokenRow;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const [windowSeconds, setWindowSeconds] = useState<number | null>(30 * DAY);
  const [limit, setLimit] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newLimit = limit.trim() === '' ? null : Number(limit);
  const limitInvalid =
    newLimit !== null &&
    (!Number.isInteger(newLimit) ||
      newLimit > 10_000 ||
      (token.maxDevices !== null && newLimit <= token.maxDevices));
  const nothingToDo = windowSeconds === null && newLimit === null;
  const canSave = !busy && !nothingToDo && !limitInvalid && reason.trim().length >= 3;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await updateEdgeInstallToken(token.id, {
        ...(windowSeconds !== null ? { ttlSeconds: windowSeconds } : {}),
        ...(newLimit !== null ? { maxDevices: newLimit } : {}),
        reason: reason.trim(),
      });
      onDone(
        `Token ${token.id.slice(0, 8)}: devices can join until ${new Date(r.expiresAt).toLocaleString()}, limit ${
          r.maxDevices ?? 'none'
        } (${r.enrolledCount} enrolled).`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'could not change the token');
    } finally {
      setBusy(false);
    }
  };

  const idBase = `extend-${token.id.slice(0, 8)}`;
  return (
    <div className="rounded-md border border-border/40 bg-muted/20 p-3 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label htmlFor={`${idBase}-window`} className="text-xs text-muted-foreground block mb-1">
            Devices can join until
          </label>
          <select
            id={`${idBase}-window`}
            value={windowSeconds === null ? '' : String(windowSeconds)}
            onChange={(e) => setWindowSeconds(e.target.value === '' ? null : Number(e.target.value))}
            className="w-full rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm"
          >
            {WINDOW_OPTIONS.map((o) => (
              <option key={o.label} value={o.seconds === null ? '' : String(o.seconds)}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="text-[11px] text-muted-foreground mt-1">
            Now: {new Date(token.expiresAt).toLocaleString()}
          </div>
        </div>
        <div>
          <label htmlFor={`${idBase}-limit`} className="text-xs text-muted-foreground block mb-1">
            New device limit (optional)
          </label>
          <input
            id={`${idBase}-limit`}
            type="number"
            min={(token.maxDevices ?? 0) + 1}
            max={10_000}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder={`more than ${token.maxDevices ?? 0}`}
            className="w-full rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm"
          />
          {limitInvalid ? (
            <div className="text-[11px] text-destructive mt-1">
              The limit can only go up (it is {token.maxDevices}), to at most 10,000. To stop new
              devices, seal the token instead.
            </div>
          ) : (
            <div className="text-[11px] text-muted-foreground mt-1">
              {token.enrolledCount} of {token.maxDevices ?? 'unlimited'} used
            </div>
          )}
        </div>
        <div>
          <label htmlFor={`${idBase}-reason`} className="text-xs text-muted-foreground block mb-1">
            Reason (recorded in the audit log)
          </label>
          <input
            id={`${idBase}-reason`}
            type="text"
            value={reason}
            maxLength={250}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Wave 3 runs another two weeks"
            className="w-full rounded border border-border/40 bg-background/40 px-2 py-1.5 text-sm"
          />
        </div>
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void save()} disabled={!canSave} data-action="disc.adm_edge_fleet.a18">
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
          Save
        </Button>
      </div>
    </div>
  );
}
